(function () {
    'use strict';

    angular
        .module('app')
        .factory('DocumentTemplate', DocumentTemplateFactory);

    DocumentTemplateFactory.$inject = ['$q', '$window', 'DocumentTemplatesService', 'DialogService', 'FileSelectorService'];

    function DocumentTemplateFactory($q, $window, DocumentTemplatesService, DialogService, FileSelectorService) {

        /**
         * Get an existing document template.
         *
         * @param {number} documentTemplateId
         * @returns {Promise} A promise that resolves a {DocumentTemplate} object for the specified documentTemplateId.
         */
        function get(documentTemplateId) {
            var docTemplatePromise = DocumentTemplatesService.getDocumentTemplate(documentTemplateId)
                .then(function (data) {
                    var documentTemplate = new DocumentTemplate(data);
                    documentTemplate.dirty = false;

                    return documentTemplate;
                });

            return docTemplatePromise;
        }

        /**
         * Create a document template using an existing document template.
         * This will also copy the file data.
         *
         * @param {number} documentTemplateId
         * @returns {Promise} A promise that resolves a {DocumentTemplate} object copied from the specified documentTemplateId.
         */
        function getCopy(documentTemplateId) {
            var docTemplatePromise = DocumentTemplatesService.getDocumentTemplate(documentTemplateId)
                .then(function (data) {
                    var documentTemplate = new DocumentTemplate(data);

                    return documentTemplate.loadFileDetails()
                        .then(function () {
                            documentTemplate.dirtyFileDetails = true;
                            delete documentTemplate.data.Id;
                            delete documentTemplate.data.UpdatedDate;
                            delete documentTemplate.data.UpdatedBy;
                            delete documentTemplate.data.CreatedDate;
                            delete documentTemplate.data.CreatedBy;
                            delete documentTemplate.data.Template;
                            delete documentTemplate.data.Name;

                            return $q.when(documentTemplate);
                        });
                });

            return docTemplatePromise;
        }

        /**
         * Create a document template with the supplied data.
         *
         * @returns {Promise} A promise that resolves a {DocumentTemplate} object.
         */
        function createWithData(documentTemplateData) {
            var documentTemplate = new DocumentTemplate(documentTemplateData);
            documentTemplate.dirty = false;

            return $q.when(documentTemplate);
        }

        /**
         * Get a new document template.
         *
         * @param {int} entityTemplateId - Preset the entity template to be used for the document template. Optional.
         *
         * @returns {Promise} A promise that resolves a new {DocumentTemplate} object.
         */
        function getNew(entityTemplateId) {
            var documentTemplate = new DocumentTemplate();
            documentTemplate.data.EntityTemplateId = entityTemplateId;
            documentTemplate.data.SaveToCurrentEntity = true;
            documentTemplate.dirty = false;

            return $q.when(documentTemplate);
        }

        /**
         * Gets a list of document templates.
         *
         * @param {int} pageSize - The page sizing for the document template list if using paging. Optional.
         * @param {int} currentPage - The current page for the document template list if using paging. Optional.
         * @param {string} genericSearch - 'Generic' filter for the document template. This matches against the name or description.
         * @param {int} entityTemplateId - The entity template assigned to the document template. Optional.
         *
         * @returns {Promise} A promise that resolves an array of {DocumentTemplate} objects for the specified parameters.
         */
        function getDocumentTemplates(pageSize, currentPage, genericSearch, entityTemplateId) {
            return DocumentTemplatesService.getDocumentTemplates(pageSize, currentPage, genericSearch, entityTemplateId)
                .then(function (data) {
                    var documentTemplates = [];

                    angular.forEach(data.DocumentTemplates, function (documentTemplateData) {
                        documentTemplates.push(new DocumentTemplate(documentTemplateData));
                    });

                    data.DocumentTemplates = documentTemplates;

                    return data;
                });
        }

        /**
         * Present the user with a Save As / Download dialog for the document template file.
         *
         * @param {DocumentTemplate} documentTemplate
         */
        function downloadTemplate(documentTemplate) {
            if (documentTemplate.fileDetails.Mode !== 'html' && documentTemplate.fileDetails.Mode !== 'txt' && _.isNil(documentTemplate.fileDetails.File)) {
                return DialogService.alert('Document Template does not have a template file.');
            }

            var blob;
            var blobName;
                        
            if (documentTemplate.fileDetails.Mode === 'html') {
                blob = new Blob([documentTemplate.fileDetails.Html]);
                blobName = 'doc-template.html';
            } else if (documentTemplate.fileDetails.Mode === 'txt') {
                blob = new Blob([documentTemplate.fileDetails.Txt]);
                blobName = 'doc-template.txt';
            } else {
                blob = documentTemplate.fileDetails.File;
                blobName = documentTemplate.fileDetails.File.name;
            }

            $window.saveAs(blob, blobName);
        }

        /**
         * @constructor
         * 
         * @param {object} data - Document template data used to create the Document Template object.
         *                  This is optional, if no data is supplied, a 'new' document template object will be created.
         *
         * @property {Object} data - Document template data, refer C# class 'DocumentTemplate'.
         * @property {string} data.Template - Reference to template file in DMS.
         * @property {string} data.OutputFormat - Preferred output format to be used when the template is merged, e.g. Docx, Pdf.
         * @property {string} data.UsageType - Where the template can be used, e.g. Email Subject, Document.
         * @property {Object} fileDetails - Details of the 'file' for the document template, e.g. the PDF including the binary data.
         * @property {bool} fileDetailsLoaded - Read only. fileDetails, due to its potentially large size, is loaded asynchronously,
         *   this property indicates if it's finished loading.
         * @property {bool} fileSelected - Read only. True if the document template has a file associated.
         * @property {Array} validOutputFormats - Read only. A list of the valid output formats for the template, this is dependant
         *   on the template format.
         * @property {bool} allowTestMerge - Read only. True if the properties of the document template permits a test merge.
         */
        function DocumentTemplate(data) {
            var vm = this;
            vm.fileDetails = {};

            if (!_.isNil(data)) {
                vm.data = data;
                vm.fileDetailsLoaded = false;
            } else {
                vm.data = {
                    Name: '',
                    Description: '',
                    Template: '',
                    MappedFields: [],
                    EntityTemplateId: null,
                    OutputFormat: 'Docx',
                    UsageType: 'Document',
                    FileDescription: '',
                    Tags: [],
                    FileName: '',
                    Active: true
                };
                vm.fileDetailsLoaded = true;
            }

            Object.defineProperty(vm, 'fileSelected', { get: getFileSelected });
            Object.defineProperty(vm, 'validOutputFormats', { get: getValidOutputFormats });
            Object.defineProperty(vm, 'templateFormat', { get: getTemplateFormat, set: setTemplateFormat });
            Object.defineProperty(vm, 'usageType', { get: getUsageType, set: setUsageType });
            Object.defineProperty(vm, 'usageTypeDetails', { get: getUsageTypeDetails });
            Object.defineProperty(vm, 'allowTestMerge', { get: getAllowTestMerge });
            Object.defineProperty(vm, 'dirty', { get: getDirty, set: setDirty });
            Object.defineProperty(vm, 'dirtyFileDetails', { get: getDirtyFileDetails, set: function (bool) { fileDetailsDirty = bool; } });
            Object.defineProperty(vm, 'templateFileName', { get: getTemplateFileName });
            vm.dirty = false;

            vm.loadFileDetails = loadFileDetails;
            vm.setFile = setFile;
            vm.delete = deleteDocumentTemplate;

            var outputFormats = {
                txt: ['Txt'],
                html: ['Html', 'Docx', 'Pdf'],
                doc: ['Docx', 'Pdf'],
                docx: ['Docx', 'Pdf'],
                pdf: ['Pdf']
            };

            /**
             * @returns {bool} true if there is a file included in the document template (PDF, HTML, etc.)
             */
            function getFileSelected() {
                var result = false;

                if (angular.isDefined(vm.fileDetails.Mode) && vm.fileDetails.Mode !== null && vm.fileDetails.Mode !== '') {
                    result = true;
                }

                return result;
            }

            /**
             * @returns {Array} An array of strings representing the valid output formats for the template.
             *   Valid output formats differ depending on the template format and the type of usage.
             */
            function getValidOutputFormats() {
                var result = [];

                // vm.fileDetails.Mode is only set once file data is fetched from the server.
                // Metadata (vm.data.Extension) is loaded first, so if "Mode" is null, rely on
                // the metadata instead.

                var fileExtension = null;
                if (!_.isNil(vm.fileDetails) && !_.isNil(vm.fileDetails.Mode)) {
                    fileExtension = vm.fileDetails.Mode;
                } else if (!_.isNil(vm.data.Extension)) {
                    fileExtension = vm.data.Extension;
                }

                if (!_.isNil(fileExtension)) {
                    result = angular.copy(outputFormats[fileExtension]);

                    if (!_.isNil(vm.data.UsageType) && vm.data.UsageType !== '') {
                        var selectedUsageType = _.find(DocumentTemplate.UsageTypes, function (usageType) {
                            return usageType.Reference === vm.data.UsageType;
                        });

                        if (!_.isNil(selectedUsageType)) {
                            result = _.select(result, function (outputFormat) {
                                return _.some(selectedUsageType.OutputFormats, function (typeOutputFormat) {
                                    return outputFormat === typeOutputFormat;
                                });
                            });
                        }
                    }
                }

                // If there are no results (due to a file not yet being uploaded/attached to the document template), default to using the output formats for docx files.
                if (result.length === 0) {
                    result = angular.copy(outputFormats.docx);
                }

                if (!angular.equals(result, getValidOutputFormats.outputFormats)) {
                    getValidOutputFormats.outputFormats = result;
                }

                return getValidOutputFormats.outputFormats;
            }

            function getUsageType() {
                return vm.data.UsageType;
            }

            function setUsageType(newValue) {
                vm.data.UsageType = newValue;

                if (!_.isNil(newValue)) {
                    setFile(vm.usageTypeDetails.DefaultFormat.toLowerCase());
                }
            }

            function getUsageTypeDetails() {
                return _.find(DocumentTemplate.UsageTypes, function (usageType) {
                    return usageType.Reference === vm.data.UsageType;
                });
            }

            function getTemplateFileName() {
                var result = null;

                if (vm.fileSelected && !_.isNil(vm.fileDetails) && !_.isNil(vm.fileDetails.File)) {
                    if (!_.isNil(vm.fileDetails.File.name) && vm.fileDetails.File.name !== '') {
                        result = vm.fileDetails.File.name;
                    } else if (!_.isNil(vm.fileDetails.File.$ngfName) && vm.fileDetails.File.$ngfName !== '') {
                        result = vm.fileDetails.File.$ngfName;
                    }
                }

                return result;
            }

            function getTemplateFormat() {
                var result = '';

                switch (vm.fileDetails.Mode) {
                    case 'txt':
                        result = 'plainText';
                        break;
                    case 'html':
                        result = 'richText';
                        break;
                    default:
                        result = 'fileUpload';
                        break;
                }

                return result;
            }

            function setTemplateFormat(newFormat) {
                if (newFormat === 'fileUpload') {
                    setFile('docx');
                } else if (newFormat === 'richText') {
                    setFile('html');
                } else if (newFormat === 'plainText') {
                    setFile('txt');
                }
            }

            /**
             * @returns {bool} true if enough properties of the document template are set to permit a test merge.
             */
            function getAllowTestMerge() {
                if (!_.isNil(vm.data.EntityTemplateId) &&
                    vm.data.EntityTemplateId !== '' &&
                    angular.isDefined(vm.fileDetails.Mode) &&
                    vm.fileDetails.Mode !== null &&
                    vm.fileDetails.Mode !== '') {

                    return true;
                } else {
                    return false;
                }
            }

            /**
             * Load the file details for the document template. This includes a server call to fetch the template file.
             *
             * @returns {Promise} A promise resolved once the load is complete.
             */
            function loadFileDetails() {
                if (angular.isUndefined(vm.data.Template) || vm.data.Template === '' || vm.data.Template === null) {
                    vm.fileDetailsLoaded = true;
                    // No template specified, so return null
                    return $q.when(null);
                }

                return DocumentTemplatesService.getDocumentFile(vm.data.Id)
                    .then(getDocumentFileOk);

                function getDocumentFileOk(response) {
                    var fileName = response.headers('Content-Disposition').match(/filename[^;\n=]*=((['"]).*?\2|[^;\n]*)/)[1];
                    fileName = fileName.replace(/^"/, '');
                    fileName = fileName.replace(/"$/, '');
                    vm.fileDetails.Mode = getModeFromFileName(fileName);

                    if (vm.fileDetails.Mode === 'html') {
                        vm.fileDetails.Html = decodeURIComponent(escape(String.fromCharCode.apply(null, new Uint8Array(response.data))));
                        originalHtml = angular.copy(vm.fileDetails.Html);
                    } else if (vm.fileDetails.Mode === 'txt') {
                        vm.fileDetails.Txt = decodeURIComponent(escape(String.fromCharCode.apply(null, new Uint8Array(response.data))));
                        originalTxt = angular.copy(vm.fileDetails.Txt);
                    } else if (vm.fileDetails.Mode === 'docx' || vm.fileDetails.Mode === 'doc' || vm.fileDetails.Mode === 'pdf') {
                        vm.fileDetails.File = new Blob([response.data]);
                        vm.fileDetails.File.name = fileName;
                    }

                    vm.fileDetailsLoaded = true;
                }
            }

            /**
             * For the provided document template file name, determine the "mode", aka template format.
             *
             * @returns {string}
             */
            function getModeFromFileName(fileName) {
                if (angular.isUndefined(fileName) || fileName === null || fileName === '') {
                    return '';
                }

                var fileNameParts = fileName.split('.');
                var extension = fileNameParts[fileNameParts.length - 1];

                switch (extension.toLowerCase()) {
                    case 'doc':
                        return 'doc';
                    case 'docx':
                        return 'docx';
                    case 'pdf':
                        return 'pdf';
                    case 'html':
                    case 'htm':
                        return 'html';
                    case 'txt':
                        return 'txt';
                    default:
                        return '';
                }
            }

            /**
             * Set the file for the document template.
             *
             * @param {string} fileType - The type of file, e.g. 'docx', 'pdf'
             * @param {File} file - The file content, refer https://developer.mozilla.org/en/docs/Web/API/File, 
             *   may be null if the file type permits. E.g. HTML permits an empty / blank file.
             * @param {bool} keepPdfInsertionDetails - If false, the pdf pdf insertion details will be removed. Optional.
             */
            function setFile(fileType, file, keepPdfInsertionDetails) {
                // Clear existing file details
                for (var prop in vm.fileDetails) {
                    if (vm.fileDetails.hasOwnProperty(prop)) {
                        delete vm.fileDetails[prop];
                    }
                }

                // Remove the Pdf editor related settings if required
                if (!_.isNil(keepPdfInsertionDetails) && !keepPdfInsertionDetails) {
                    if (!_.isNil(vm.data) && !_.isNil(vm.data.AdvancedSettings) && !_.isNil(vm.data.AdvancedSettings.PdfInsertionDetails)) {
                        vm.data.AdvancedSettings.PdfInsertionDetails = [];
                    }
                }

                // If fileType and file have not been supplied, it means the file has been removed. Thus the file details dirty must be set to true.
                if (_.isNil(fileType) && _.isNil(file)) {
                    fileDetailsDirty = true;
                }

                // The file details mode should only be set if it's html, txt or a file has been provided.
                if (fileType === 'html' || fileType === 'txt' || !_.isNil(file)) {
                    vm.fileDetails.Mode = fileType;
                } else {
                    vm.fileDetails.Mode = '';
                }

                // Set the default output format. If there is an existing output format set that is valid for the new file type, it should not be changed.
                if (vm.validOutputFormats !== null && vm.validOutputFormats.length > 0) {
                    if (angular.isDefined(vm.data.OutputFormat) && vm.data.OutputFormat !== null && vm.data.OutputFormat !== '' &&
                        _.some(vm.validOutputFormats, function (n) { return n === vm.data.OutputFormat; })) {
                        // If the output format is still valid, don't change it.
                    } else {
                        vm.data.OutputFormat = vm.validOutputFormats[0];
                    }
                }

                originalHtml = null;
                originalTxt = null;

                switch (fileType) {
                    case 'doc':
                    case 'docx':
                    case 'pdf':
                        vm.fileDetails.FileType = fileType;
                        vm.fileDetails.File = file;
                        fileDetailsDirty = true;
                        break;
                    case 'html':
                        vm.fileDetails.Html = '';
                        originalHtml = '';
                        fileDetailsDirty = true;
                        break;
                    case 'txt':
                        vm.fileDetails.Txt = '';
                        originalTxt = '';
                        fileDetailsDirty = true;
                        break;
                }

                vm.fileDetailsLoaded = true;
                vm.dirty = true;
            }

            // For dirty checking
            var originalData = {};
            var originalHtml = null;
            var originalTxt = null;
            var fileDetailsDirty = false;

            /**
             * @returns {bool} true if the model has changed since it was last specified as not dirty.
             */
            function getDirty() {
                if (!angular.equals(originalData, vm.data)) {
                    return true;
                }

                return getDirtyFileDetails();
            }

            /**
             * @returns {bool} true if the vm.fileDetails has been changed since dirty was last set to false, or since first loading.
             */
            function getDirtyFileDetails() {
                if (vm.fileDetailsLoaded) {
                    if (fileDetailsDirty) {
                        return true;
                    }

                    if (vm.fileDetails.Mode === 'txt') {
                        return !angular.equals(originalTxt, vm.fileDetails.Txt);
                    }

                    if (vm.fileDetails.Mode === 'html') {
                        return !angular.equals(originalHtml, vm.fileDetails.Html);
                    }
                }

                return false;
            }

            /**
             * @param {bool} Specify whether the model is dirty.
             */
            function setDirty(newDirty) {
                if (newDirty) {
                    originalData = {};
                } else {
                    originalData = angular.copy(vm.data);
                    originalHtml = null;
                    originalTxt = null;
                    fileDetailsDirty = false;

                    if (vm.fileDetails.Mode === 'txt') {
                        originalTxt = angular.copy(vm.fileDetails.Txt);
                    } else if (vm.fileDetails.Mode === 'html') {
                        originalHtml = angular.copy(vm.fileDetails.Html);
                    }
                }
            }

            /**
             * Shows a 'confirm deletion dialog' and then deletes the document template if 'Ok' is pressed.
             *
             * @returns {promise} There are three distinct states for the promise:
             *                  Fulfilled - The document template was successfully deleted
             *                  Rejected with null value - The user 'cancelled' the confirm dialog, no attempt was made to delete the document template
             *                  Rejected with value - An attempt was made to delete the document template however it was not successful
             */
            function deleteDocumentTemplate() {
                if (!_.isNil(vm.data.Id) && vm.data.Id !== '') {
                    return DialogService.show({
                        locals: { documentTemplateIds: [vm.data.Id] },
                        controller: 'DeleteDocumentTemplateDialog',
                        controllerAs: 'vm',
                        templateUrl: 'app/documenttemplate/delete-document-template-dialog.tmpl.html'
                    })
                        .then(function () {
                            return DocumentTemplatesService.deleteDocumentTemplate(vm.data.Id);
                        }, function () {
                            return $q.reject(null);
                        });
                } else {
                    return $q.reject('Document Template does not have an Id value.');
                }
            }
        }

        DocumentTemplate.prototype = {};
        DocumentTemplate.prototype.constructor = DocumentTemplate;

        DocumentTemplate.get = get;
        DocumentTemplate.getCopy = getCopy;
        DocumentTemplate.getNew = getNew;
        DocumentTemplate.createWithData = createWithData;
        DocumentTemplate.getDocumentTemplates = getDocumentTemplates;
        DocumentTemplate.downloadTemplate = downloadTemplate;
        /*
         * The document template usage types data structure contains various details about how a particular type can be used within ChilliDocs. If adding a new usage type, this data structure MUST be updated.
         * Reference - This is the value used for populating 'UsageType' on a document template. Changing this value would require the persisted document template data to also be updated to ensure correct operation.
         * Name - The display name for the usage type. E.g. this will be shown in the 'Select Usage Type' select list.
         * OutputFormats - This is a collection of output formats that can be used for a usage type.
         * DefaultFormat - This is the default file format for the usage type.
         */
        DocumentTemplate.UsageTypes = {
            Document: { Reference: 'Document', Name: 'Document', OutputFormats: ['Pdf', 'Docx', 'Doc', 'Html', 'Txt'], DefaultFormat: 'Docx' },
            EmailBody: { Reference: 'EmailBody', Name: 'Email Body', OutputFormats: ['Html', 'Txt'], DefaultFormat: 'Html' },
            EmailSubject: { Reference: 'EmailSubject', Name: 'Email Subject', OutputFormats: ['Txt'], DefaultFormat: 'Txt' },
            EmailPreheader: { Reference: 'EmailPreheader', Name: 'Email Preheader', OutputFormats: ['Html', 'Txt'], DefaultFormat: 'Html' },
            SMS: { Reference: 'SMS', Name: 'SMS', OutputFormats: ['Txt'], DefaultFormat: 'Txt' }
        };

        return DocumentTemplate;
    }
})();